"use client";

import { Star, MapPin, TrendingUp, Zap, Award, Home, Building2 } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";
import Link from "next/link";

const successStories = [
  {
    id: 1,
    name: "Adebayo Fashola",
    initials: "AF",
    location: "Lagos",
    type: "Residential",
    accent: "from-[#002301] to-[#004d03]",
    systemSize: "5kW",
    savings: "840,000",
    story: "Switching to solar was the best decision for my family. We have eliminated power cuts completely and our electricity bills dropped by 90%. Ren Power Group made finding the right installer easy.",
    rating: 5,
    installation: "2024"
  },
  {
    id: 2,
    name: "Green Valley Hotel",
    initials: "GV",
    location: "Abuja",
    type: "Commercial",
    accent: "from-[#004d03] to-[#17BC2E]",
    systemSize: "50kW",
    savings: "4,200,000",
    story: "Our hotel now runs entirely on solar during the day. Generator costs dropped dramatically, and our guests appreciate our commitment to sustainability. The ROI was faster than expected.",
    rating: 5,
    installation: "2023"
  },
  {
    id: 3,
    name: "Chioma Nwosu",
    initials: "CN",
    location: "Port Harcourt",
    type: "Residential",
    accent: "from-[#002301] to-[#006b05]",
    systemSize: "3kW",
    savings: "450,000",
    story: "As a remote worker, stable power is crucial. Solar gave me energy independence and peace of mind. I am saving money and helping the environment at the same time.",
    rating: 5,
    installation: "2024"
  },
  {
    id: 4,
    name: "TechHub Innovation Center",
    initials: "TH",
    location: "Lagos",
    type: "Commercial",
    accent: "from-[#17BC2E] to-[#002301]",
    systemSize: "100kW",
    savings: "8,500,000",
    story: "Our tech hub needed reliable 24/7 power. Solar with battery backup solved all our power issues. We have not used generators in months. The environment and our budget both benefit.",
    rating: 5,
    installation: "2023"
  },
  {
    id: 5,
    name: "Musa Ibrahim",
    initials: "MI",
    location: "Kano",
    type: "Residential",
    accent: "from-[#003d02] to-[#17BC2E]",
    systemSize: "7kW",
    savings: "980,000",
    story: "Living in Northern Nigeria with abundant sunshine, solar was the obvious choice. My system produces more than I need. It is reliable, cost-effective, and maintenance-free.",
    rating: 5,
    installation: "2024"
  },
  {
    id: 6,
    name: "Blessing Pharmaceuticals",
    initials: "BP",
    location: "Enugu",
    type: "Commercial",
    accent: "from-[#002301] to-[#005c04]",
    systemSize: "25kW",
    savings: "2,800,000",
    story: "Temperature-sensitive medications require constant refrigeration. Solar ensures uninterrupted power for our cold storage. It has been a game-changer for our business continuity.",
    rating: 5,
    installation: "2023"
  }
];

export default function SuccessStoriesPage() {
  const [selectedType, setSelectedType] = useState("All");

  const filtered = selectedType === "All"
    ? successStories
    : successStories.filter((s) => s.type === selectedType);

  return (
    <div className="min-h-screen bg-[#f4f6f6]">
      {/* Hero */}
      <div className="bg-[#002301] pt-28 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg width="100%" height="100%">
            <defs><pattern id="ss-dots" width="30" height="30" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" /></pattern></defs>
            <rect width="100%" height="100%" fill="url(#ss-dots)" />
          </svg>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFB600]/8 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Star className="w-3.5 h-3.5 text-[#FFB600]" />
            <span className="text-white/80 text-xs font-medium">Customer Testimonials</span>
          </span>
          <h1 className="text-white font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5">
            Real People.<br />
            Real <span className="text-[#FFB600]">Solar Success.</span>
          </h1>
          <p className="text-white/65 text-lg max-w-2xl mx-auto mb-12">
            Thousands of Nigerians are saving money, gaining energy independence, and reducing carbon footprint with solar.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
            {[
              { value: "5,000+", label: "Happy Customers" },
              { value: "98%", label: "Satisfaction Rate" },
              { value: "15B+", label: "Naira Saved" },
            ].map(({ value, label }) => (
              <div key={label} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <p className="text-[#FFB600] font-bold text-3xl mb-1">{value}</p>
                <p className="text-white/60 text-xs">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border-b border-gray-100 py-5 sticky top-0 z-40 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 flex flex-wrap justify-center gap-3">
          {["All", "Residential", "Commercial"].map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-200 ${
                selectedType === type
                  ? "bg-[#002301] text-white shadow-md"
                  : "bg-[#f4f6f6] text-gray-600 hover:bg-gray-200"
              }`}
            >
              {type === "Residential" && <Home className="w-3.5 h-3.5" />}
              {type === "Commercial" && <Building2 className="w-3.5 h-3.5" />}
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">

        {/* Featured story */}
        {filtered.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`bg-gradient-to-br ${filtered[0].accent} rounded-3xl overflow-hidden mb-8 shadow-lg`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 lg:p-10 flex flex-col justify-between">
                <div>
                  <span className={`inline-block text-xs font-bold px-3 py-1.5 rounded-full mb-5 ${filtered[0].type === "Residential" ? "bg-[#FFB600] text-[#002301]" : "bg-white/20 text-white border border-white/30"}`}>
                    {filtered[0].type}
                  </span>
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(filtered[0].rating)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-[#FFB600] text-[#FFB600]" />
                    ))}
                  </div>
                  <p className="text-white/90 text-lg lg:text-xl leading-relaxed font-light">
                    <span className="text-[#FFB600] text-5xl leading-none font-serif mr-1">&ldquo;</span>
                    {filtered[0].story}
                    <span className="text-[#FFB600] text-4xl leading-none font-serif ml-1">&rdquo;</span>
                  </p>
                </div>
                <div className="mt-8 flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center border border-white/30 flex-shrink-0">
                    <span className="text-white font-bold text-lg">{filtered[0].initials}</span>
                  </div>
                  <div>
                    <p className="text-white font-bold text-base">{filtered[0].name}</p>
                    <div className="flex items-center gap-1 text-white/60 text-sm mt-0.5">
                      <MapPin className="w-3.5 h-3.5" />{filtered[0].location}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-black/20 p-8 lg:p-10 flex flex-col justify-center gap-5">
                {[
                  { icon: Zap, label: "System Size", value: filtered[0].systemSize, color: "text-[#FFB600]" },
                  { icon: TrendingUp, label: "Annual Savings", value: `NGN ${filtered[0].savings}`, color: "text-[#17BC2E]" },
                  { icon: Award, label: "Year Installed", value: filtered[0].installation, color: "text-white" },
                ].map(({ icon: Icon, label, value, color }) => (
                  <div key={label} className="flex items-center gap-4 bg-white/10 rounded-2xl px-5 py-4 border border-white/10">
                    <Icon className={`w-5 h-5 ${color} flex-shrink-0`} />
                    <div>
                      <p className="text-white/50 text-xs">{label}</p>
                      <p className={`${color} font-bold text-lg`}>{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Grid of remaining stories */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.slice(1).map((story, i) => (
            <motion.div
              key={story.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex flex-col"
            >
              <div className={`h-1.5 bg-gradient-to-r ${story.accent}`} />
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 bg-gradient-to-br ${story.accent} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <span className="text-white font-bold text-sm">{story.initials}</span>
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm leading-tight">{story.name}</p>
                      <div className="flex items-center gap-1 text-gray-400 text-xs mt-0.5">
                        <MapPin className="w-3 h-3" />{story.location}
                      </div>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${story.type === "Residential" ? "bg-amber-50 text-amber-700" : "bg-green-50 text-green-700"}`}>
                    {story.type}
                  </span>
                </div>

                <div className="flex gap-0.5 mb-3">
                  {[...Array(story.rating)].map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5 fill-[#FFB600] text-[#FFB600]" />
                  ))}
                </div>

                <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-5">
                  <span className="text-[#FFB600] text-2xl font-serif leading-none mr-0.5">&ldquo;</span>
                  {story.story}
                  <span className="text-[#FFB600] text-2xl font-serif leading-none ml-0.5">&rdquo;</span>
                </p>

                <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-50">
                  <div className="text-center">
                    <p className="text-[#002301] font-bold text-sm">{story.systemSize}</p>
                    <p className="text-gray-400 text-xs">System</p>
                  </div>
                  <div className="text-center border-x border-gray-100">
                    <p className="text-[#FFB600] font-bold text-xs truncate">{story.savings}</p>
                    <p className="text-gray-400 text-xs">Saved/yr</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[#002301] font-bold text-sm">{story.installation}</p>
                    <p className="text-gray-400 text-xs">Year</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 bg-[#002301] rounded-3xl p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-10">
            <svg width="100%" height="100%"><defs><pattern id="cta-dots" width="25" height="25" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" /></pattern></defs><rect width="100%" height="100%" fill="url(#cta-dots)" /></svg>
          </div>
          <div className="relative z-10">
            <h2 className="text-white font-bold text-2xl sm:text-3xl mb-3">Ready to Write Your Own Story?</h2>
            <p className="text-white/60 text-base mb-7 max-w-xl mx-auto">Join thousands of Nigerians who have made the switch to clean, affordable solar energy.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/marketplace" className="inline-flex items-center justify-center gap-2 bg-[#FFB600] text-[#002301] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#17BC2E] hover:text-white transition-all duration-300">
                Browse Solar Products
              </Link>
              <Link href="/solar-calculator" className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/25 text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-white/20 transition-all duration-300">
                Calculate My Savings
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
